export function sanitizeRedirectTarget(target) {
  if (!target || typeof target !== "string") return null;
  if (!target.startsWith("/") || target.startsWith("//")) return null;

  const fallbackOrigin = "http://localhost";
  const currentOrigin =
    typeof window !== "undefined" && window.location?.origin && window.location.origin !== "null"
      ? window.location.origin
      : fallbackOrigin;

  try {
    const parsedTarget = new URL(target, currentOrigin);
    if (parsedTarget.origin !== currentOrigin) return null;
    return `${parsedTarget.pathname}${parsedTarget.search}${parsedTarget.hash}`;
  } catch (_) {
    return null;
  }
}

function isPublicHomeRedirectTarget(target) {
  return target === "/" || target.startsWith("/?") || target.startsWith("/#");
}

function isQuickplayEntryRedirectTarget(target) {
  return target === "/play" || target.startsWith("/play?") || target.startsWith("/play#");
}

function toParsedAppUrl(target) {
  const redirectTarget = sanitizeRedirectTarget(target);
  if (!redirectTarget) return null;

  try {
    return new URL(redirectTarget, "http://localhost");
  } catch (_) {
    return null;
  }
}

function extractLeagueSlugFromPath(pathname) {
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) return null;
  if (segments[0] === "league" && segments[1]) return segments[1];
  if (segments[0].startsWith(":")) return null;

  const globalNonLeagueRoots = new Set([
    "login",
    "register",
    "password-reset",
    "password-reset-request",
    "invite",
    "about",
    "contacts",
    "contact",
    "policy",
    "privacy",
    "terms",
    "terms-of-service",
    "onboarding",
    "more",
    "join-league",
    "switch-league",
    "create-league",
    "create-team",
  ]);

  return globalNonLeagueRoots.has(segments[0]) ? null : segments[0];
}

function isAlwaysSafePostAuthPath(pathname) {
  return (
    pathname === "/play" ||
    pathname === "/play-with-friends" ||
    pathname === "/onboarding" ||
    pathname === "/more" ||
    pathname === "/create-league" ||
    pathname.startsWith("/invite/")
  );
}

export function buildOAuthCallbackUrl(redirectTarget, extraParams = {}) {
  const origin =
    typeof window !== "undefined" && window.location?.origin && window.location.origin !== "null"
      ? window.location.origin
      : "http://localhost";
  const callbackUrl = new URL("/auth/callback", origin);
  const safeRedirectTarget = sanitizeRedirectTarget(redirectTarget);

  if (safeRedirectTarget) {
    callbackUrl.searchParams.set("redirect", safeRedirectTarget);
  }

  Object.entries(extraParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      callbackUrl.searchParams.set(key, String(value));
    }
  });

  return callbackUrl.toString();
}

function hasMatchingLeagueId(leagueArray, leagueId) {
  if (!leagueId) return false;

  return leagueArray.some((league) => {
    const candidateId = league?._id ?? league?.id ?? null;
    return candidateId != null && String(candidateId) === String(leagueId);
  });
}

export function resolveLoginRedirectTarget(target) {
  const redirectTarget = sanitizeRedirectTarget(target);
  if (!redirectTarget || isPublicHomeRedirectTarget(redirectTarget)) {
    return null;
  }

  return redirectTarget;
}

export function getUserLeagueArray(userLeagues, user) {
  return Array.isArray(userLeagues) && userLeagues.length > 0
    ? userLeagues
    : (Array.isArray(user?.leagues) ? user.leagues : []);
}

export function resolveDefaultPostLoginPath(userLeagues, user) {
  const leagueArray = getUserLeagueArray(userLeagues, user);

  if (leagueArray.length === 0) {
    return "/onboarding";
  }

  if (leagueArray.length === 1) {
    const onlyLeague = leagueArray[0];
    return onlyLeague?.slug ? `/${onlyLeague.slug}/table` : "/switch-league";
  }

  return "/switch-league";
}

export function resolvePostAuthDestination({
  redirectTarget,
  userLeagues,
  user,
  isGuestUpgrade = false,
  quickplayState = null,
}) {
  if (isGuestUpgrade) {
    const authorizedRedirectTarget = resolveAuthorizedPostAuthRedirectTarget(
      redirectTarget,
      userLeagues,
      user
    );
    // An upgrading guest is already inside Quick Play, so returning to the
    // Quick Play entry page would restart the funnel. The fallback below
    // already picks the better Quick Play destination.
    if (authorizedRedirectTarget && !isQuickplayEntryRedirectTarget(authorizedRedirectTarget)) {
      return authorizedRedirectTarget;
    }

    const quickplayLeagueSlug = quickplayState?.league?.slug;
    return quickplayLeagueSlug
      ? `/${quickplayLeagueSlug}/transfers-players`
      : resolveDefaultPostLoginPath(userLeagues, user);
  }

  return (
    resolveAuthorizedPostAuthRedirectTarget(redirectTarget, userLeagues, user) ||
    resolveDefaultPostLoginPath(userLeagues, user)
  );
}

export function resolveAuthorizedPostAuthRedirectTarget(target, userLeagues, user) {
  const parsedTarget = toParsedAppUrl(target);
  if (!parsedTarget) return null;

  const redirectTarget = `${parsedTarget.pathname}${parsedTarget.search}${parsedTarget.hash}`;
  if (isPublicHomeRedirectTarget(redirectTarget)) {
    return null;
  }

  const leagueArray = getUserLeagueArray(userLeagues, user);

  if (isAlwaysSafePostAuthPath(parsedTarget.pathname)) {
    return redirectTarget;
  }

  if (parsedTarget.pathname === "/switch-league") {
    return leagueArray.length > 1 ? redirectTarget : null;
  }

  if (parsedTarget.pathname === "/create-team") {
    return hasMatchingLeagueId(leagueArray, parsedTarget.searchParams.get("leagueId"))
      ? redirectTarget
      : null;
  }

  const leagueSlug = extractLeagueSlugFromPath(parsedTarget.pathname);
  if (!leagueSlug) {
    return null;
  }

  return leagueArray.some((league) => league?.slug === leagueSlug) ? redirectTarget : null;
}

export function buildLoginRedirectPath(location) {
  const redirectTarget = `${location.pathname}${location.search || ""}${location.hash || ""}`;
  return `/login?redirect=${encodeURIComponent(redirectTarget)}`;
}

export function buildRegisterRedirectPath(target) {
  const redirectTarget = sanitizeRedirectTarget(target);
  return redirectTarget
    ? `/register?redirect=${encodeURIComponent(redirectTarget)}`
    : "/register";
}
